import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/productService';
import api from '../services/api';
import { DefaultEditor, EditorProvider } from 'react-simple-wysiwyg';
import './CreateProduct.css';

const CreateProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState('');
  const [primaryImage, setPrimaryImage] = useState(null);
  const [primaryImagePreview, setPrimaryImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);
  const [colors, setColors] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discounted_price: '',
    category_id: '',
    sku: '',
    stock_quantity: 0,
    is_best_seller: false,
    is_new_arrival: false,
    status: 'active',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await api.get('/products/categories');
      setCategories(response.data.data?.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
      // Don't show error, just continue without categories
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePrimaryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrimaryImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrimaryImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImages((prev) => [...prev, ...files]);
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAdditionalImagesPreview((prev) => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePrimaryImage = () => {
    setPrimaryImage(null);
    setPrimaryImagePreview(null);
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
    setAdditionalImagesPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const addColor = () => {
    setColors((prev) => [...prev, { name: '', hex_code: '#000000' }]);
  };

  const updateColor = (index, field, value) => {
    setColors((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const removeColor = (index) => {
    setColors((prev) => prev.filter((_, i) => i !== index));
  };

  const addHighlight = () => {
    setHighlights((prev) => [...prev, { label: '', value: '' }]);
  };

  const updateHighlight = (index, field, value) => {
    setHighlights((prev) =>
      prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
    );
  };

  const removeHighlight = (index) => {
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Product name is required');
      }
      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error('Valid price is required');
      }
      if (!formData.sku.trim()) {
        throw new Error('SKU is required');
      }
      if (!primaryImage) {
        throw new Error('Primary image is required');
      }

      const cleanedColors = colors
        .map((c) => ({
          name: (c.name || '').trim(),
          hex_code: (c.hex_code || '').trim(),
        }))
        .filter((c) => c.name || c.hex_code);
      for (let i = 0; i < cleanedColors.length; i++) {
        if (!cleanedColors[i].name) {
          throw new Error(`Color #${i + 1}: name is required`);
        }
        if (!/^#[0-9A-Fa-f]{6}$/.test(cleanedColors[i].hex_code)) {
          throw new Error(`Color #${i + 1}: pick a valid color`);
        }
      }

      // Blank rows are dropped rather than rejected, so an empty row left over
      // from "+ Add highlight" does not block the save. display_order follows
      // the on-screen order.
      const cleanedHighlights = highlights
        .map((h) => ({
          label: (h.label || '').trim(),
          value: (h.value || '').trim(),
        }))
        .filter((h) => h.label || h.value)
        .map((h, i) => ({
          label: h.label || null,
          value: h.value,
          display_order: i,
        }));
      for (let i = 0; i < cleanedHighlights.length; i++) {
        if (!cleanedHighlights[i].value) {
          throw new Error(`Highlight #${i + 1}: value is required`);
        }
      }

      // Create FormData for multipart/form-data
      const formDataToSend = new FormData();

      // Add text fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('price', formData.price);
      if (formData.discounted_price && formData.discounted_price.trim() !== '') {
        formDataToSend.append('discounted_price', formData.discounted_price);
      }
      formDataToSend.append('sku', formData.sku);
      formDataToSend.append('stock_quantity', formData.stock_quantity || 0);
      formDataToSend.append('is_best_seller', formData.is_best_seller);
      formDataToSend.append('is_new_arrival', formData.is_new_arrival);
      formDataToSend.append('status', formData.status);

      if (formData.category_id) {
        formDataToSend.append('category_id', formData.category_id);
      }

      // Add colors as JSON string (multipart can't carry arrays directly)
      formDataToSend.append('colors', JSON.stringify(cleanedColors));
      formDataToSend.append('highlights', JSON.stringify(cleanedHighlights));

      // Add primary image
      formDataToSend.append('primaryImage', primaryImage);

      // Add additional images
      additionalImages.forEach((image) => {
        formDataToSend.append('images', image);
      });

      await productService.createProduct(formDataToSend);
      navigate('/products', { state: { message: 'Product created successfully!' } });
    } catch (err) {
      let errorMessage = 'Failed to create product';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-product-container">
      <div className="page-header">
        <h1 className="page-title">Create Product</h1>
        <button
          onClick={() => navigate('/products')}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-group">
            <label htmlFor="name">
              Product Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter product name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <div className="editor-container">
              <EditorProvider>
                <DefaultEditor
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }));
                  }}
                  containerProps={{ style: { height: '300px', backgroundColor: '#fff' } }}
                />
              </EditorProvider>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">
                Price <span className="required">*</span>
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="discounted_price">
                Discounted Price (Optional)
              </label>
              <input
                type="number"
                id="discounted_price"
                name="discounted_price"
                value={formData.discounted_price}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sku">
                SKU <span className="required">*</span>
              </label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                required
                placeholder="e.g., PROD-001"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stock_quantity">Stock Quantity</label>
              <input
                type="number"
                id="stock_quantity"
                name="stock_quantity"
                value={formData.stock_quantity}
                onChange={handleChange}
                min="0"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category_id">Category</label>
              {categoriesLoading ? (
                <select disabled>
                  <option>Loading categories...</option>
                </select>
              ) : (
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Colors (Optional)</label>
            <div className="colors-list">
              {colors.map((color, index) => (
                <div key={index} className="color-row">
                  <input
                    type="color"
                    value={color.hex_code}
                    onChange={(e) =>
                      updateColor(index, 'hex_code', e.target.value)
                    }
                    className="color-swatch-input"
                    aria-label={`Color ${index + 1} hex`}
                  />
                  <input
                    type="text"
                    value={color.name}
                    onChange={(e) => updateColor(index, 'name', e.target.value)}
                    placeholder="Color name (e.g., Midnight Black)"
                    className="color-name-input"
                  />
                  <button
                    type="button"
                    onClick={() => removeColor(index)}
                    className="btn-remove-color"
                    aria-label="Remove color"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addColor}
                className="btn-add-color"
              >
                + Add color
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Highlights (Optional)</label>
            <p className="field-hint">
              Bullet points shown on the product page. Leave the label blank for
              a plain line such as &quot;1 Year Warranty&quot;.
            </p>
            <div className="highlights-list">
              {highlights.map((highlight, index) => (
                <div key={index} className="highlight-row">
                  <input
                    type="text"
                    value={highlight.label}
                    onChange={(e) =>
                      updateHighlight(index, 'label', e.target.value)
                    }
                    placeholder="Label (e.g., Movement)"
                    className="highlight-label-input"
                    maxLength={100}
                    aria-label={`Highlight ${index + 1} label`}
                  />
                  <input
                    type="text"
                    value={highlight.value}
                    onChange={(e) =>
                      updateHighlight(index, 'value', e.target.value)
                    }
                    placeholder="Value (e.g., Japanese Quartz)"
                    className="highlight-value-input"
                    maxLength={255}
                    aria-label={`Highlight ${index + 1} value`}
                  />
                  <button
                    type="button"
                    onClick={() => removeHighlight(index)}
                    className="btn-remove-color"
                    aria-label={`Remove highlight ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addHighlight}
                className="btn-add-color"
              >
                + Add highlight
              </button>
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_best_seller"
                checked={formData.is_best_seller}
                onChange={handleChange}
              />
              <span>Mark as Best Seller</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_new_arrival"
                checked={formData.is_new_arrival}
                onChange={handleChange}
              />
              <span>Mark as New Arrival</span>
            </label>
          </div>

          {/* Primary Image Upload */}
          <div className="form-group">
            <label htmlFor="primaryImage">
              Primary Image <span className="required">*</span>
            </label>
            {primaryImagePreview ? (
              <div className="image-preview-container">
                <img
                  src={primaryImagePreview}
                  alt="Primary preview"
                  className="image-preview"
                />
                <button
                  type="button"
                  onClick={removePrimaryImage}
                  className="btn-remove-image"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="file-upload-area">
                <input
                  type="file"
                  id="primaryImage"
                  accept="image/*"
                  onChange={handlePrimaryImageChange}
                  required
                  className="file-input"
                />
                <label htmlFor="primaryImage" className="file-upload-label">
                  <span className="upload-icon">📷</span>
                  <span>Click to upload primary image</span>
                  <span className="file-hint">JPG, PNG, WEBP (Max 5MB)</span>
                </label>
              </div>
            )}
          </div>

          {/* Additional Images Upload */}
          <div className="form-group">
            <label htmlFor="additionalImages">
              Additional Images (Optional)
            </label>
            {additionalImagesPreview.length > 0 && (
              <div className="images-preview-grid">
                {additionalImagesPreview.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img
                      src={preview}
                      alt={`Additional ${index + 1}`}
                      className="image-preview-small"
                    />
                    <button
                      type="button"
                      onClick={() => removeAdditionalImage(index)}
                      className="btn-remove-image-small"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="file-upload-area">
              <input
                type="file"
                id="additionalImages"
                accept="image/*"
                multiple
                onChange={handleAdditionalImagesChange}
                className="file-input"
              />
              <label htmlFor="additionalImages" className="file-upload-label">
                <span className="upload-icon">🖼️</span>
                <span>Click to upload additional images</span>
                <span className="file-hint">You can select multiple images (Max 10, 5MB each)</span>
              </label>
            </div>
          </div>

          {/* Error message at bottom */}
          {error && (
            <div className="error-message-bottom">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;
